/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  AccountManagementProtectedRoute,
  AccountManagementProvider,
  PAYMENTS_ROUTE_PERMISSION,
  PaymentsRouter as BasePaymentsRouter,
  PaymentsSkeleton,
} from "@farmlands/account-management";
import { AppError } from "@farmlands/ui-library";
import { FC } from "react";
import { Navigate } from "react-router-dom";

import { PageContainer } from "../../../components/layout/PageContainer";
import { useThemeBackgroundColor } from "../../../hooks/useThemeBackgroundColor";
import { HOME_ROUTE } from "../../../util/routing";
import { useAccountManagementContextValue } from "../hooks/useAccountManagementContextValue";

export const PaymentsRouter: FC = () => {
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const { contextValue, isLoading, error, refetch } = useAccountManagementContextValue();

  if (isLoading) {
    return (
      <PageContainer>
        <PaymentsSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return <AppError contactUrl={contextValue.config.contactUrl} onTryAgainClick={refetch} />;
  }

  if (!contextValue.account.selectedAccount) {
    return <Navigate to={HOME_ROUTE} replace />;
  }

  return (
    <AccountManagementProvider value={contextValue}>
      <AccountManagementProtectedRoute
        {...PAYMENTS_ROUTE_PERMISSION}
        redirectTo={HOME_ROUTE}
        loadingFallback={
          <PageContainer>
            <PaymentsSkeleton />
          </PageContainer>
        }
      >
        <PageContainer>
          <BasePaymentsRouter />
        </PageContainer>
      </AccountManagementProtectedRoute>
    </AccountManagementProvider>
  );
};

export default PaymentsRouter;
